import React from 'react'
import isFunction from 'lodash/isFunction'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import Box from '@material-ui/core/Box'
// @src imports
import { LEGACY_SITE_URL } from 'src/app/api'
import { Icon, ProfileAvatar, SuspenseBoundary, Transition } from 'src/chrome'
import { IconTypeOrIconString } from 'src/app/types'
import { compact } from 'src/helpers'
import {
  useAccount,
  useActions,
  useQueries,
  useSelector,
  useState,
  useVertical,
} from 'src/hooks'

import styles from './bottomNav.module.scss'
import { getNotificationCount } from '../../graphql'
import { InterpolationWithTheme } from '@emotion/core'
import { Text } from '@sendoutcards/quantum-design-ui'

const NOTIFICATIONS_ITEM = 'NOTIFICATIONS_ITEM'
const PAYMENT_SETTINGS_ITEM = 'PAYMENT_SETTINGS_ITEM'
const CARD_HISTORY_ITEM = 'CARD_HISTORY_ITEM'
const DRAFTED_ORDERS_ITEM = 'DRAFTED_ORDERS_ITEM'
const DRAFTED_CARDS_ITEM = 'DRAFTED_CARDS_ITEM'
export const RELATIONSHIPS_ITEM = 'RELATIONSHIPS_ITEM'
const CAMPAIGNS_ITEM = 'CAMPAIGNS_ITEM'
export const CATALOG_ITEM = 'CATALOG_ITEM'
const GIFTS_ITEM = 'GIFTS_ITEM'
const OPEN_HELP_ITEM = 'OPEN_HELP_ITEM'
const CLOSE_HELP_ITEM = 'CLOSE_HELP_ITEM'
const PRIVACY_ITEM = 'PRIVACY_ITEM'
const HELP_MENU_ITEM = 'HELP_MENU_ITEM'
const MESSAGE_ITEM = 'MESSAGE_ITEM'
const TIPS_ITEM = 'TIPS_ITEM'

type Item =
  | typeof NOTIFICATIONS_ITEM
  | typeof PAYMENT_SETTINGS_ITEM
  | typeof CARD_HISTORY_ITEM
  | typeof DRAFTED_ORDERS_ITEM
  | typeof DRAFTED_CARDS_ITEM
  | typeof RELATIONSHIPS_ITEM
  | typeof CAMPAIGNS_ITEM
  | typeof CATALOG_ITEM
  | typeof GIFTS_ITEM
  | typeof OPEN_HELP_ITEM
  | typeof CLOSE_HELP_ITEM
  | typeof PRIVACY_ITEM
  | typeof HELP_MENU_ITEM
  | typeof MESSAGE_ITEM
  | typeof TIPS_ITEM

export const defaultItems: (isMobile: boolean) => Item[] = isMobile =>
  compact(
    CATALOG_ITEM,
    RELATIONSHIPS_ITEM,
    CAMPAIGNS_ITEM,
    GIFTS_ITEM,
    isMobile ? OPEN_HELP_ITEM : undefined,
  )

export const dashboardItems: (isMobile: boolean) => Item[] = isMobile =>
  compact(
    PAYMENT_SETTINGS_ITEM,
    CARD_HISTORY_ITEM,
    DRAFTED_ORDERS_ITEM,
    DRAFTED_CARDS_ITEM,
    CATALOG_ITEM,
    isMobile ? OPEN_HELP_ITEM : undefined,
  )

const helpItems: Item[] = compact(
  HELP_MENU_ITEM,
  PRIVACY_ITEM,
  MESSAGE_ITEM,
  TIPS_ITEM,
  CLOSE_HELP_ITEM,
)

type Button = {
  id: string
  icon: IconTypeOrIconString
  label: string
  onClick: () => void
  viewBox?: string
  hidden?: boolean
  badge?: string | number | null
  color?: string
  disabled?: boolean
}

type Dispatch = {
  openAccount: () => void
  openNotifications: () => void
  openCardHistory: () => void
  openDraftedOrders: () => void
  openDraftedCards: () => void
  openRelationshipManager: () => void
  openCampaigns: () => void
  openCatalog: () => void
  openGiftStore: () => void
  openCampaignStore: () => void
  openPaymentSettings: () => void
}

const buttonsForItems: (
  tourId: string | undefined,
  dispatch: Dispatch,
  notificationCount: number | string | null,
  helpButtonAction: () => void,
) => { [key in Item]: Button } = (
  tourId,
  dispatch,
  notificationCount,
  helpButtonAction,
) => ({
  NOTIFICATIONS_ITEM: {
    id: 'goto_notifications_btn',
    icon: 'BELL',
    label: 'Notifications',
    onClick: dispatch.openNotifications,
  },
  PAYMENT_SETTINGS_ITEM: {
    id: 'goto_payment_settings_btn',
    icon: 'CREDITCARD',
    label: 'Payment Settings',
    onClick: dispatch.openPaymentSettings,
  },
  CARD_HISTORY_ITEM: {
    id: 'goto_card_history_btn',
    icon: 'ENVELOPE',
    label: 'Card History',
    onClick: dispatch.openCardHistory,
  },
  DRAFTED_ORDERS_ITEM: {
    id: 'goto_drafted_orders_btn',
    icon: 'LIST',
    label: 'Drafted Orders',
    onClick: dispatch.openDraftedOrders,
  },
  DRAFTED_CARDS_ITEM: {
    id: 'goto_drafted_cards_btn',
    icon: 'FLATCARD',
    label: 'Drafted Cards',
    onClick: dispatch.openDraftedCards,
  },
  RELATIONSHIPS_ITEM: {
    id: 'goto_contacts_btn',
    icon: 'CONTACT',
    label: 'Relationships',
    onClick: dispatch.openRelationshipManager,
  },
  CAMPAIGNS_ITEM: {
    id: 'goto_campaigns_btn',
    icon: 'CLIPBOARD',
    label: 'Campaigns',
    onClick: dispatch.openCampaigns,
  },
  CATALOG_ITEM: {
    id: 'goto_catalog_btn',
    icon: 'SOCLOGO',
    label: 'Catalog',
    onClick: dispatch.openCatalog,
  },
  GIFTS_ITEM: {
    id: 'goto_gift_store_btn',
    icon: 'TAG',
    label: 'Gifts',
    onClick: dispatch.openGiftStore,
  },
  OPEN_HELP_ITEM: {
    id: 'open_help_items_btn',
    icon: 'QUESTIONMARK',
    label: 'Help',
    onClick: helpButtonAction,
  },
  CLOSE_HELP_ITEM: {
    id: 'close_help_items_btn',
    icon: 'CLOSE',
    label: 'Close',
    onClick: helpButtonAction,
  },
  PRIVACY_ITEM: {
    id: 'goto_privacy_btn',
    icon: 'COG',
    label: 'Privacy',
    onClick: () => {
      return window.open(
        'http://help.sendoutcards.com/en/articles/3603758-california-consumer-privacy-act-ccpa',
        '_blank',
        'noreferrer',
      )
    },
  },
  HELP_MENU_ITEM: {
    id: 'goto_help_btn',
    icon: 'LIST',
    label: 'Help',
    onClick: () => {
      return window.open(
        'https://help.sendoutcards.com',
        '_blank',
        'noreferrer',
      )
    },
  },
  MESSAGE_ITEM: {
    id: 'goto_message_btn',
    icon: 'CHAT',
    label: 'Message',
    onClick: () => {
      if (window.Intercom) {
        window.Intercom('show')
      } else {
        console.log(
          'Intercom Does not exist on the window, please check your intercom config script',
        )
      }
    },
  },
  TIPS_ITEM: {
    id: 'goto_tips_btn',
    icon: 'INFO',
    label: 'Tips',
    onClick: () => window.Appcues.show(tourId),
  },
})

type ButtonComponentProps = {
  button: Button
  sidebarClass: string
}

const ButtonComponent: React.FC<ButtonComponentProps> = props => {
  const { button, sidebarClass } = props
  const iconSize = 22
  const iconColor = '#6f8394'

  return (
    <span
      id={button.id}
      className={`${styles.button} ${sidebarClass}`}
      onClick={button.disabled ? () => {} : () => button.onClick()}
    >
      <Icon
        icon={button.icon}
        size={iconSize}
        viewBox={button.viewBox}
        color={iconColor}
      />
      {button.badge && (
        <span className={styles.notificationBadge}>{button.badge}</span>
      )}
      <span
        className={styles.iconLabel}
        style={
          !button.disabled && button.color
            ? { color: button.color }
            : { color: '#484646' }
        }
      >
        {button.label}
      </span>
    </span>
  )
}

type Props = {
  items: Array<Item | Button> | ((isMobile: boolean) => Array<Item | Button>)
  rootCSS?: InterpolationWithTheme<unknown>
  isSidebarEnabled?: boolean
}

const UnconnectedBottomNav: React.FC<Props> = props => {
  const { children, items, isSidebarEnabled, rootCSS } = props

  const account = useAccount()
  const actions = useActions()
  const { id } = useVertical()
  const isClientSOC = id === 'soc'
  const { tourId } = useSelector(state => state.helpmenu)
  const { isMobile } = useSelector(state => state.window)

  const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const sidebarClass = () => {
    return isSidebarEnabled ? styles.column : null
  }

  const [notificationCountResult] = useQueries(getNotificationCount())

  const notificationCount =
    notificationCountResult > 0
      ? notificationCountResult <= 10
        ? notificationCountResult
        : '10+'
      : null

  const buttonsLookup = buttonsForItems(
    tourId,
    actions,
    notificationCount,
    () => setIsHelpMenuOpen(!isHelpMenuOpen),
  )

  const buttons = (isHelpMenuOpen
    ? helpItems
    : isFunction(items)
    ? items(isMobile)
    : items
  ).map(item => (typeof item === 'string' ? buttonsLookup[item] : item))

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <div css={rootCSS}>
      <div className={`${styles.wrapper} ${sidebarClass()}`}>
        {children ? (
          <div
            className={`${styles.navRow} ${styles.children} ${sidebarClass()}`}
          >
            {children}
          </div>
        ) : (
          <div
            className={`${styles.navRow} ${sidebarClass()}`}
            style={{
              justifyContent:
                isMobile && isClientSOC
                  ? 'space-between'
                  : isMobile
                  ? 'space-around'
                  : undefined,
            }}
          >
            <Box
              display="flex"
              p={1}
              alignItems="center"
              style={{ padding: 0 }}
            >
              <Box p={1} flexGrow={1}>
                <button
                  id="avatar_btn"
                  aria-controls="drop-down-menu"
                  aria-haspopup="true"
                  onClick={handleClick}
                  style={{ border: 'none', background: 'none' }}
                >
                  <ProfileAvatar
                    account={account}
                    size={32}
                    style={{
                      zIndex: 300,
                      fontSize: '1.8rem',
                      color: '#fff',
                      letterSpacing: 1,
                    }}
                    isEditable={false}
                  />
                </button>
                <Menu
                  id="drop-down-menu"
                  anchorEl={anchorEl}
                  keepMounted={false}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  <MenuItem
                    id="goto_account_btn"
                    onClick={actions.openAccount}
                    style={{ fontSize: 'initial', padding: '3px 16px' }}
                  >
                    <Text type="body">{`Account #${account.genealogyId}`}</Text>
                  </MenuItem>
                  {!account.isNewUser && (
                    <MenuItem
                      onClick={() =>
                        (window.location.href = `${LEGACY_SITE_URL}/main-menu/?access_token=${account.token}`)
                      }
                      style={{ fontSize: 'initial', padding: '3px 16px' }}
                    >
                      <Text type="body">{'Legacy Site'}</Text>
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() =>
                      (window.location.href = `${LEGACY_SITE_URL}/resources/`)
                    }
                    style={{ fontSize: 'initial', padding: '3px 16px' }}
                  >
                    <Text type="body">{'Resource Center'}</Text>
                  </MenuItem>
                  <MenuItem
                    id="logout_btn"
                    onClick={actions.logout}
                    style={{ fontSize: 'initial', padding: '3px 16px' }}
                  >
                    <Text type="body">{'Logout'}</Text>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
            {buttons
              .filter(button => !button.hidden)
              .map((button, index) => {
                return isClientSOC ? (
                  <ButtonComponent
                    key={index}
                    button={button}
                    sidebarClass={sidebarClass()}
                  />
                ) : button.id === 'goto_payment_settings_btn' ? (
                  <ButtonComponent
                    key={index}
                    button={button}
                    sidebarClass={sidebarClass()}
                  />
                ) : null
              })}
          </div>
        )}
      </div>
    </div>
  )
}

export const BottomNav = (props: Props) => (
  <SuspenseBoundary
    unresolved={<Transition message={'Loading...'} />}
    failure={error => `Failed loading: ${error}`}
  >
    <UnconnectedBottomNav {...props} />
  </SuspenseBoundary>
)
